/**
 * Demo asset management system
 * Handles loading, caching, and optimization of demo-specific assets
 */

import { getFinalDemoConfig } from '../config/demo.config';

export interface AssetConfig {
  path: string;
  type: 'image' | 'video' | 'audio' | 'document' | 'font';
  preload: boolean;
  lazy: boolean;
  fallback?: string;
  variants?: Record<string, string>;
}

export interface AssetManifest {
  logos: Record<string, AssetConfig>;
  images: Record<string, AssetConfig>;
  videos: Record<string, AssetConfig>;
  fonts: Record<string, AssetConfig>;
  documents: Record<string, AssetConfig>;
}

class AssetManager {
  private config = getFinalDemoConfig();
  private cache = new Map<string, any>();
  private loadingPromises = new Map<string, Promise<any>>();

  // Asset manifest for demo
  private manifest: AssetManifest = {
    logos: {
      'stablepay-full': {
        path: '/demo-assets/logos/stablepay-logo-full.svg',
        type: 'image',
        preload: true,
        lazy: false,
        variants: {
          'full-color': '/demo-assets/logos/stablepay-logo-full.svg',
          'white': '/demo-assets/logos/stablepay-logo-white.svg',
          'monochrome': '/demo-assets/logos/stablepay-logo-mono.svg',
        },
      },
      'stablepay-icon': {
        path: '/demo-assets/logos/stablepay-icon.svg',
        type: 'image',
        preload: true,
        lazy: false,
        variants: {
          'full-color': '/demo-assets/logos/stablepay-icon.svg',
          'white': '/demo-assets/logos/stablepay-icon-white.svg',
          'monochrome': '/demo-assets/logos/stablepay-icon-mono.svg',
        },
      },
      'favicon': {
        path: '/demo-assets/logos/favicon.ico',
        type: 'image',
        preload: true,
        lazy: false,
      },
    },
    
    images: {
      'provider-hashkey': {
        path: '/demo-assets/providers/hashkey-logo.png',
        type: 'image',
        preload: false,
        lazy: true,
      },
      'provider-osl': {
        path: '/demo-assets/providers/osl-logo.png',
        type: 'image',
        preload: false,
        lazy: true,
      },
      'provider-moonpay': {
        path: '/demo-assets/providers/moonpay-logo.png',
        type: 'image',
        preload: false,
        lazy: true,
      },
      'stablecoin-usdt': {
        path: '/demo-assets/stablecoins/usdt-logo.png',
        type: 'image',
        preload: false,
        lazy: true,
      },
      'stablecoin-usdc': {
        path: '/demo-assets/stablecoins/usdc-logo.png',
        type: 'image',
        preload: false,
        lazy: true,
      },
      'stablecoin-hkdt': {
        path: '/demo-assets/stablecoins/hkdt-logo.png',
        type: 'image',
        preload: false,
        lazy: true,
      },
      'stablecoin-cnht': {
        path: '/demo-assets/stablecoins/cnht-logo.png',
        type: 'image',
        preload: false,
        lazy: true,
      },
      'settlement-fps': {
        path: '/demo-assets/settlement/fps-logo.png',
        type: 'image',
        preload: false,
        lazy: true,
      },
      'settlement-cips': {
        path: '/demo-assets/settlement/cips-logo.png',
        type: 'image',
        preload: false,
        lazy: true,
      },
      'settlement-ecny': {
        path: '/demo-assets/settlement/ecny-logo.png',
        type: 'image',
        preload: false,
        lazy: true,
      },
    },
    
    videos: {
      'demo-walkthrough': {
        path: '/demo-assets/videos/demo-walkthrough.mp4',
        type: 'video',
        preload: false,
        lazy: true,
        fallback: '/demo-assets/images/demo-walkthrough-poster.jpg',
      },
      'provider-failover': {
        path: '/demo-assets/videos/provider-failover-demo.mp4',
        type: 'video',
        preload: false,
        lazy: true,
        fallback: '/demo-assets/images/provider-failover-poster.jpg',
      },
    },
    
    fonts: {
      'inter-regular': {
        path: '/demo-assets/fonts/Inter-Regular.woff2',
        type: 'font',
        preload: true,
        lazy: false,
      },
      'inter-medium': {
        path: '/demo-assets/fonts/Inter-Medium.woff2',
        type: 'font',
        preload: true,
        lazy: false,
      },
      'inter-semibold': {
        path: '/demo-assets/fonts/Inter-SemiBold.woff2',
        type: 'font',
        preload: true,
        lazy: false,
      },
    },
    
    documents: {
      'demo-guide': {
        path: '/demo-assets/docs/demo-guide.pdf',
        type: 'document',
        preload: false,
        lazy: true,
      },
      'feature-overview': {
        path: '/demo-assets/docs/feature-overview.pdf',
        type: 'document',
        preload: false,
        lazy: true,
      },
    },
  };

  /**
   * Get asset URL with CDN support
   */
  getAssetUrl(category: keyof AssetManifest, name: string, variant?: string): string {
    const asset = this.manifest[category]?.[name];
    if (!asset) {
      console.warn(`Asset not found: ${category}/${name}`);
      return '';
    }

    let path = asset.path;
    
    // Use variant if specified and available
    if (variant && asset.variants?.[variant]) {
      path = asset.variants[variant];
    }

    // Apply CDN prefix if enabled
    if (this.buildConfig.deployment.enableCDN && this.buildConfig.deployment.cdnUrl) {
      return `${this.buildConfig.deployment.cdnUrl}${path}`;
    }

    // Apply asset prefix
    return `${this.buildConfig.deployment.assetPrefix}${path}`;
  }

  /**
   * Get logo URL based on current configuration
   */
  getLogoUrl(type: 'full' | 'icon' = 'full'): string {
    const logoKey = type === 'full' ? 'stablepay-full' : 'stablepay-icon';
    const variant = this.config.assets.logoVariant;
    return this.getAssetUrl('logos', logoKey, variant);
  }

  /**
   * Preload critical assets
   */
  async preloadCriticalAssets(): Promise<void> {
    const criticalAssets: Array<{ category: keyof AssetManifest; name: string }> = [];
    
    // Collect all assets marked for preloading
    Object.entries(this.manifest).forEach(([category, assets]) => {
      Object.entries(assets).forEach(([name, config]) => {
        if (config.preload) {
          criticalAssets.push({ category: category as keyof AssetManifest, name });
        }
      });
    });

    // Preload assets in parallel
    const preloadPromises = criticalAssets.map(({ category, name }) => 
      this.loadAsset(category, name)
    );

    try {
      await Promise.all(preloadPromises);
      console.log('Critical assets preloaded successfully');
    } catch (error) {
      console.warn('Some critical assets failed to preload:', error);
    }
  }

  /**
   * Load asset with caching
   */
  async loadAsset(category: keyof AssetManifest, name: string, variant?: string): Promise<any> {
    const cacheKey = `${category}/${name}${variant ? `/${variant}` : ''}`;
    
    // Return cached asset if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return existing loading promise if in progress
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    // Start loading
    const loadingPromise = this.performAssetLoad(category, name, variant);
    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const asset = await loadingPromise;
      this.cache.set(cacheKey, asset);
      return asset;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  /**
   * Perform actual asset loading
   */
  private async performAssetLoad(category: keyof AssetManifest, name: string, variant?: string): Promise<any> {
    const url = this.getAssetUrl(category, name, variant);
    const asset = this.manifest[category]?.[name];
    
    if (!asset) {
      throw new Error(`Asset not found: ${category}/${name}`);
    }

    switch (asset.type) {
      case 'image':
        return this.loadImage(url, asset.fallback);
      case 'video':
        return this.loadVideo(url, asset.fallback);
      case 'font':
        return this.loadFont(url);
      case 'document':
        return this.loadDocument(url);
      default:
        throw new Error(`Unsupported asset type: ${asset.type}`);
    }
  }

  /**
   * Load image with fallback
   */
  private loadImage(url: string, fallback?: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => resolve(img);
      img.onerror = () => {
        if (fallback) {
          const fallbackImg = new Image();
          fallbackImg.onload = () => resolve(fallbackImg);
          fallbackImg.onerror = () => reject(new Error(`Failed to load image: ${url} and fallback: ${fallback}`));
          fallbackImg.src = fallback;
        } else {
          reject(new Error(`Failed to load image: ${url}`));
        }
      };
      
      img.src = url;
    });
  }

  /**
   * Load video with fallback
   */
  private loadVideo(url: string, fallback?: string): Promise<HTMLVideoElement> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      
      video.onloadeddata = () => resolve(video);
      video.onerror = () => {
        if (fallback) {
          // Load fallback image instead
          this.loadImage(fallback)
            .then(() => resolve(video))
            .catch(() => reject(new Error(`Failed to load video: ${url} and fallback: ${fallback}`)));
        } else {
          reject(new Error(`Failed to load video: ${url}`));
        }
      };
      
      video.src = url;
      video.preload = 'metadata';
    });
  }

  /**
   * Load font
   */
  private async loadFont(url: string): Promise<FontFace> {
    const fontName = url.split('/').pop()?.split('.')[0] || 'unknown';
    const fontFace = new FontFace(fontName, `url(${url})`);
    
    try {
      const loadedFont = await fontFace.load();
      document.fonts.add(loadedFont);
      return loadedFont;
    } catch (error) {
      throw new Error(`Failed to load font: ${url}`);
    }
  }

  /**
   * Load document
   */
  private async loadDocument(url: string): Promise<Response> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response;
    } catch (error) {
      throw new Error(`Failed to load document: ${url}`);
    }
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  /**
   * Check if asset exists in manifest
   */
  hasAsset(category: keyof AssetManifest, name: string): boolean {
    return !!(this.manifest[category]?.[name]);
  }

  /**
   * Get all assets in a category
   */
  getAssetsInCategory(category: keyof AssetManifest): Record<string, AssetConfig> {
    return this.manifest[category] || {};
  }
}

// Singleton instance
export const assetManager = new AssetManager();

// React hook for asset loading
export const useAsset = (category: keyof AssetManifest, name: string, variant?: string) => {
  const [asset, setAsset] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    let mounted = true;

    const loadAsset = async () => {
      try {
        setLoading(true);
        setError(null);
        const loadedAsset = await assetManager.loadAsset(category, name, variant);
        if (mounted) {
          setAsset(loadedAsset);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadAsset();

    return () => {
      mounted = false;
    };
  }, [category, name, variant]);

  return { asset, loading, error };
};

// React hook for logo URL
export const useLogoUrl = (type: 'full' | 'icon' = 'full'): string => {
  return assetManager.getLogoUrl(type);
};

// Add React import
import React from 'react';